import axios from "axios";
import ApiService, { errorMessage } from "helpers/ApiHelper";
import baseURL from "config";

const ROOT = "/api/v1/newsletter";

export { errorMessage };

/**
 * Builds the query string shared by the list and the CSV export — same
 * filters, so a downloaded file always matches what the table is showing.
 */
const buildQuery = ({ page, limit, lang, q, status } = {}) => {
  const params = new URLSearchParams();
  if (page) params.set("page", page);
  if (limit) params.set("limit", limit);
  if (lang) params.set("lang", lang);
  if (q) params.set("q", q);
  if (status) params.set("status", status);
  const query = params.toString();
  return query ? `?${query}` : "";
};

/**
 * Admin listing of newsletter subscribers, newest first.
 *
 * `ApiService.request` already unwraps the `{status, data}` envelope down to
 * `data`, so this resolves straight to `{ subscribers, total, page, limit }`.
 */
export const fetchSubscribers = (filters) =>
  ApiService.request(`${ROOT}${buildQuery(filters)}`, "get");

/**
 * Downloads the CSV export for the current filters.
 *
 * Bypasses `ApiService.request` — that helper parses every response as the
 * `{ status, data }` JSON envelope, which a CSV body is not — and asks axios
 * directly for a blob, carrying the same bearer token every other admin call
 * uses. The caller turns the blob into an object URL and clicks a throwaway
 * link, since a CSV download isn't itself a navigable route.
 */
export const downloadSubscribersCsv = async (filters) => {
  const token = localStorage.getItem("token");
  const response = await axios.get(`${baseURL}${ROOT}/export.csv${buildQuery(filters)}`, {
    responseType: "blob",
    headers: token ? { Authorization: `Bearer ${token}` } : undefined,
  });

  const filenameMatch = /filename="?([^"]+)"?/.exec(
    response.headers["content-disposition"] || ""
  );
  const filename = filenameMatch ? filenameMatch[1] : "newsletter-subscribers.csv";

  const url = window.URL.createObjectURL(response.data);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  // Deferred: revoking synchronously can race the click's download in some
  // browsers, which drops the file before it's saved. A 0ms timeout pushes
  // the revoke to the next tick, after the browser has already grabbed it.
  setTimeout(() => window.URL.revokeObjectURL(url), 0);
};
